import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';

interface useSearchParamsReturnProps {
    setSearchParam: (key: string, value: string) => void;
    removeSearchParam: (key: string) => void;
    getAllSearchParams: () => { [x: string]: string | undefined | null };
    getSearchParam: (key: string) => string;
    clearSearchParams: (paramsToMantain?: URLSearchParams) => void;
    goBack: () => void;
    // eslint-disable-next-line no-restricted-globals
    navigate: ReturnType<typeof useNavigate>;
}

interface useSearchParamsProps {
    preserveState?: boolean;
}

/**
 * @author Renan Salustiano <renan.salustano@ter.grupomarista.org.br>
 * @param props
 */
export const useSearchParams = (
    props: useSearchParamsProps = { preserveState: true },
): useSearchParamsReturnProps => {
    const { preserveState } = props;
    const { search, pathname } = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(search);

    const changeHistory = (params: URLSearchParams): void => {
        const search = params.toString();
        if (preserveState) {
            navigate(pathname + (search ? `?${search}` : ''));
        } else {
            navigate(pathname + (search ? `?${search}` : ''), { replace: true });
        }
    };

    const setSearchParam = (key: string, value: string): void => {
        searchParams.set(key, value);
        changeHistory(searchParams);
    };

    const removeSearchParam = (key: string): void => {
        searchParams.delete(key);
        changeHistory(searchParams);
    };

    const getSearchParam = (key: string): string => {
        return searchParams.get(key) ?? '';
    };

    const getAllSearchParams = (): {
        [x: string]: string | undefined | null;
    } => {
        return qs.parse(searchParams.toString()) as {
            [x: string]: string | undefined | null;
        };
    };

    const clearSearchParams = (paramsToMantain?: URLSearchParams): void => {
        changeHistory(paramsToMantain || new URLSearchParams());
    };

    const goBack = () => {
        navigate(-1);
    };

    return {
        setSearchParam,
        removeSearchParam,
        getSearchParam,
        getAllSearchParams,
        goBack,
        clearSearchParams,
        navigate,
    };
};
